import {
	IRegEdMetadataField,
	IRegEdMetadataForm,
	IRegEdMetadataOption,
	RegEdFieldType,
} from '@Queries/RegEdCompliance/types';
import * as Api from '@ViewModels';
import moment from 'moment';
import { CampaignType } from '../../../../models/AdminModels';
import { computeVisibleFields } from '../../../../surveys/containers/SurveyResponses/shared/hooks/useConditionalVisibility';

// `Object.values` on a string enum yields its members in declaration order, so the array index
// matches the integer ordinal an endpoint may send in place of the string member name.
const fieldTypesByOrdinal = Object.values(RegEdFieldType);

export const normalizeFieldType = (raw: unknown): RegEdFieldType | undefined => {
	if (typeof raw === 'string') {
		return (fieldTypesByOrdinal as string[]).includes(raw) ? (raw as RegEdFieldType) : undefined;
	}
	if (typeof raw === 'number') {
		return fieldTypesByOrdinal[raw];
	}
	return undefined;
};

// Options frequently omit `externalId`, and imported `visibleWhen.fieldValue` rules reference the
// option LABEL — so the label is the reliable answer value (`externalId`, when present, equals it).
export const regEdOptionValue = (option: IRegEdMetadataOption): string => option.externalId ?? option.label ?? '';

// Imported catalog strings, not a Levitate enum. No newsletter entry: the catalog offers both a weekly and
// a monthly/quarterly option, and a Levitate newsletter carries no cadence to choose between them.
const CONTENT_TYPE_ANSWER_BY_CAMPAIGN_TYPE: Partial<Record<CampaignType, string>> = {
	[CampaignType.Blog]: 'Website',
	[CampaignType.Email]: 'Letter/Email/Text',
	[CampaignType.Social]: 'Social Media Posts/Message Templates/Advertisement',
};

const CONTENT_TYPE_ANSWERS = Object.values(CONTENT_TYPE_ANSWER_BY_CAMPAIGN_TYPE);

/**
 * Identified by the field offering *every* material type, not by one value: "Website" also appears among the
 * distribution-channel options, so a single value matches several fields. Field ids come from the import and
 * the wording is the broker-dealer's, so neither is ours to key off either.
 *
 * Single-select only — a `MultipleOptions` field answers with an array, and a string would not render.
 */
export const resolveContentTypeAnswer = (
	schema: IRegEdMetadataForm | null | undefined,
	campaignType: CampaignType | undefined
): { fieldId: string; value: string } | undefined => {
	const target = campaignType ? CONTENT_TYPE_ANSWER_BY_CAMPAIGN_TYPE[campaignType] : undefined;
	if (!target) {
		return undefined;
	}
	const candidates = orderedActiveFields(schema).filter(field => {
		if (!field.id || normalizeFieldType(field.fieldType) !== RegEdFieldType.Option) {
			return false;
		}
		const values = new Set((field.options ?? []).map(o => regEdOptionValue(o).trim()));
		return CONTENT_TYPE_ANSWERS.every(answer => values.has(answer));
	});
	if (candidates.length !== 1) {
		return undefined;
	}
	const option = (candidates[0].options ?? []).find(o => regEdOptionValue(o).trim() === target);
	return option ? { fieldId: candidates[0].id!, value: regEdOptionValue(option) } : undefined;
};

/** Every field a visibility rule reads, flattening the nested logical form. */
const visibilityTriggerIds = (visibleWhen: unknown): string[] => {
	const rule = visibleWhen as { _type?: unknown; fieldId?: string; visibilities?: unknown[] } | null;
	if (!rule || typeof rule._type !== 'string') {
		return [];
	}
	if (rule.fieldId) {
		return [rule.fieldId];
	}
	return (rule.visibilities ?? []).flatMap(visibilityTriggerIds);
};

/**
 * Schema order, except a conditional field follows the one that reveals it. AdTrax orders the
 * "if other, please specify…" questions at the end of the form, so answering a checkbox halfway up
 * made its follow-up appear screens below, reading as unrelated to the thing that triggered it.
 *
 * A field whose trigger can't be resolved keeps its schema position rather than being dropped or
 * guessed at, which also makes a circular rule harmless.
 */
export const orderedActiveFields = (schema: IRegEdMetadataForm | null | undefined): IRegEdMetadataField[] => {
	const fields = (schema?.fields ?? [])
		.filter(f => f.isActive !== false && !!f.id)
		.slice()
		.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

	const byId = new Map(fields.map(f => [f.id!, f]));
	const dependentsOf = new Map<string, IRegEdMetadataField[]>();
	const roots: IRegEdMetadataField[] = [];
	for (const field of fields) {
		// The last trigger in schema order — a rule reading several fields belongs under the latest of
		// them, or it would jump above a question it depends on.
		const trigger = visibilityTriggerIds(field.visibleWhen)
			.filter(id => id !== field.id && byId.has(id))
			.sort((a, b) => fields.findIndex(f => f.id === a) - fields.findIndex(f => f.id === b))
			.pop();
		if (trigger) {
			dependentsOf.set(trigger, [...(dependentsOf.get(trigger) ?? []), field]);
		} else {
			roots.push(field);
		}
	}

	const ordered: IRegEdMetadataField[] = [];
	const placed = new Set<string>();
	const emit = (field: IRegEdMetadataField) => {
		if (placed.has(field.id!)) {
			return;
		}
		placed.add(field.id!);
		ordered.push(field);
		for (const dependent of dependentsOf.get(field.id!) ?? []) {
			emit(dependent);
		}
	};
	roots.forEach(emit);
	fields.forEach(emit);
	return ordered;
};

const hasEvaluableType = (visibleWhen: unknown): visibleWhen is Api.QuestionVisibility =>
	!!visibleWhen && typeof (visibleWhen as { _type?: unknown })._type === 'string';

// Forward `visibleWhen` only when it carries a recognizable `_type` discriminator — anything
// without one is treated as unconditionally visible rather than silently hidden.
export const toSurveyForm = (fields: IRegEdMetadataField[]): Api.SurveyForm => ({
	surveyParts: fields.map(field => {
		const part: Api.SurveyQuestion = {
			id: field.id!,
			_type: 'ShortAnswerQuestion',
			fieldId: field.id!,
			visibleWhen: hasEvaluableType(field.visibleWhen) ? field.visibleWhen : null,
		};
		return part;
	}),
});

// Multi-select answers become comma-separated so the visibility hook's `includes` check works.
export const toFormValues = (answers: Record<string, unknown>): Record<string, string> => {
	const out: Record<string, string> = {};
	for (const [key, value] of Object.entries(answers)) {
		if (Array.isArray(value)) {
			out[key] = value.map(String).join(',');
		} else if (value != null && typeof value !== 'object') {
			out[key] = String(value);
		}
	}
	return out;
};

export const isAnswerEmpty = (value: unknown): boolean =>
	value == null || value === '' || (Array.isArray(value) && value.length === 0);

// A filing must never carry answers the advisor can no longer see or retract; cascades to a
// fixpoint since clearing a controlling field can hide its dependents.
export const stripHiddenFieldAnswers = (
	schema: IRegEdMetadataForm | null | undefined,
	answers: Record<string, unknown>
): Record<string, unknown> => {
	const fields = orderedActiveFields(schema);
	const surveyForm = toSurveyForm(fields);
	if (!surveyForm.surveyParts.length) {
		return answers;
	}
	const governedFieldIds = new Set(fields.map(field => field.id!));
	let current = answers;
	for (let pass = 0; pass <= governedFieldIds.size; pass++) {
		const visible = computeVisibleFields(surveyForm, toFormValues(current));
		let changed = false;
		const next: Record<string, unknown> = {};
		for (const [key, value] of Object.entries(current)) {
			if (governedFieldIds.has(key) && !visible.has(key)) {
				changed = true;
				continue;
			}
			next[key] = value;
		}
		if (!changed) {
			return current;
		}
		current = next;
	}
	return current;
};

// Required-field validation lives client-side — the API only warns on unmapped keys rather than rejecting.
export const findMissingRequiredFieldIds = (
	schema: IRegEdMetadataForm | null | undefined,
	answers: Record<string, unknown>
): string[] => {
	const fields = orderedActiveFields(schema);
	const surveyForm = toSurveyForm(fields);
	const visible = surveyForm.surveyParts.length ? computeVisibleFields(surveyForm, toFormValues(answers)) : undefined;
	return fields
		.filter(field => {
			if (!field.isRequired || !field.id) {
				return false;
			}
			if (normalizeFieldType(field.fieldType) === RegEdFieldType.Label) {
				return false;
			}
			if (visible && !visible.has(field.id)) {
				return false;
			}
			return isAnswerEmpty(answers[field.id]);
		})
		.map(field => field.id!);
};

const PLATFORM_ANSWER_BY_PROVIDER: Partial<Record<Api.SocialMediaType, string>> = {
	[Api.SocialMediaType.Facebook]: 'Facebook Business',
	[Api.SocialMediaType.Instagram]: 'Instagram Business',
	[Api.SocialMediaType.LinkedIn]: 'LinkedIn',
};

const PLATFORM_ANSWERS = Object.values(PLATFORM_ANSWER_BY_PROVIDER).filter((v): v is string => !!v);

/**
 * Levitate cannot publish to X, so an advisor working here can never truthfully say a post will run there —
 * the option is offered by the questionnaire, not by us. Matched on the label because that is all the
 * imported schema gives; a bare `X` must match as a whole word or every label containing an x would.
 */
export const isUnpublishableToOption = (label: string | null | undefined): boolean => {
	const normalized = (label ?? '').trim().toLowerCase();
	return normalized.includes('twitter') || normalized.replace(/[^a-z0-9]/g, '') === 'x';
};

/**
 * Identified by visibility, because the platform question repeats verbatim under each material type — the
 * post, the weekly newsletter and the monthly one all offer the same four options, so nothing about the
 * options themselves tells them apart. Only the one revealed by the current answers is the right question.
 */
export const resolvePlatformAnswer = (
	schema: IRegEdMetadataForm | null | undefined,
	answers: Record<string, unknown>,
	providers: readonly Api.SocialMediaType[] | undefined
): { fieldId: string; values: string[] } | undefined => {
	const wanted = [...new Set((providers ?? []).map(p => PLATFORM_ANSWER_BY_PROVIDER[p]).filter(Boolean))];
	if (!wanted.length) {
		return undefined;
	}
	const fields = orderedActiveFields(schema);
	const surveyForm = toSurveyForm(fields);
	const visible = surveyForm.surveyParts.length ? computeVisibleFields(surveyForm, toFormValues(answers)) : undefined;
	const candidates = fields.filter(field => {
		if (!field.id || normalizeFieldType(field.fieldType) !== RegEdFieldType.MultipleOptions) {
			return false;
		}
		if (visible && !visible.has(field.id)) {
			return false;
		}
		const values = new Set((field.options ?? []).map(o => regEdOptionValue(o).trim()));
		return PLATFORM_ANSWERS.every(answer => values.has(answer));
	});
	if (candidates.length !== 1) {
		return undefined;
	}
	const offered = new Set((candidates[0].options ?? []).map(o => regEdOptionValue(o).trim()));
	const values = wanted.filter(value => offered.has(value));
	return values.length ? { fieldId: candidates[0].id!, values } : undefined;
};

/**
 * Fills what the entry point already implies, in one pass so the platform question is resolved against the
 * material answer this same call just supplied. Returns undefined when there is nothing to add.
 */
export const applyEntryPointAnswers = (
	schema: IRegEdMetadataForm | null | undefined,
	answers: Record<string, unknown>,
	campaignType: CampaignType | undefined,
	socialProviders?: readonly Api.SocialMediaType[]
): Record<string, unknown> | undefined => {
	let next = answers;
	const material = resolveContentTypeAnswer(schema, campaignType);
	if (material && isAnswerEmpty(next[material.fieldId])) {
		next = { ...next, [material.fieldId]: material.value };
	}
	const platforms = resolvePlatformAnswer(schema, next, socialProviders);
	if (platforms && isAnswerEmpty(next[platforms.fieldId])) {
		next = { ...next, [platforms.fieldId]: platforms.values };
	}
	return next === answers ? undefined : next;
};

/**
 * Drops the server's resolved answers into the bag. Defaults lose to anything already answered — on a
 * path that seeds a prior filing, overwriting would silently discard what was filed last time.
 * Returns the same object when there is nothing to add, so a caller can seed on identity change.
 */
export const applyAnswerDefaults = (
	schema: IRegEdMetadataForm | null | undefined,
	answers: Record<string, unknown>
): Record<string, unknown> => {
	const next: Record<string, unknown> = { ...answers };
	let changed = false;
	for (const field of orderedActiveFields(schema)) {
		const defaults = field.defaultValues;
		if (!field.id || !defaults?.length || !isAnswerEmpty(answers[field.id])) {
			continue;
		}
		next[field.id] =
			normalizeFieldType(field.fieldType) === RegEdFieldType.MultipleOptions ? [...defaults] : defaults[0];
		changed = true;
	}
	return changed ? next : answers;
};

/**
 * A `yyyy-MM-dd` answer, or null. Strict: a shape-only check admits impossible days like `2026-13-45`,
 * which the typed input forwards verbatim, and comparing an invalid moment returns false — so a
 * shape-only gate lets a bad date past every subsequent bound rather than rejecting it.
 */
const asIsoDay = (value: unknown): string | null =>
	typeof value === 'string' && moment(value, 'YYYY-MM-DD', true).isValid() ? value : null;

/**
 * The question asking when the material goes into use — the one date answer bounded below by review
 * completion. It is the single **visible** date question that is not the review one, the review
 * question being the only one the server marks, with `minimumDate`; ids and labels are the
 * broker-dealer's and never ours to key off.
 *
 * Undefined where that is ambiguous: with no review question there is nothing to identify the others
 * against, and where a firm asks several other date questions they cannot be told apart — a date
 * question can legitimately predate review (an expiration, a prior approval), so a guess would bound
 * or answer the wrong one. Visibility is honoured because a schema may ask for first use on mutually
 * exclusive branches, where counting hidden twins would abstain on a form that is in fact unambiguous.
 */
const resolveFirstUseField = (
	schema: IRegEdMetadataForm | null | undefined,
	answers: Record<string, unknown>
): IRegEdMetadataField | undefined => {
	const fields = orderedActiveFields(schema);
	const surveyForm = toSurveyForm(fields);
	const visible = surveyForm.surveyParts.length ? computeVisibleFields(surveyForm, toFormValues(answers)) : undefined;
	const dateFields = fields.filter(
		field => normalizeFieldType(field.fieldType) === RegEdFieldType.Date && (!visible || visible.has(field.id!))
	);
	if (!dateFields.some(field => field.minimumDate)) {
		return undefined;
	}
	const candidates = dateFields.filter(field => !field.minimumDate);
	return candidates.length === 1 ? candidates[0] : undefined;
};

/** Which question the review-completion floor applies to, for the form to bound that picker alone. */
export const resolveFirstUseFieldId = (
	schema: IRegEdMetadataForm | null | undefined,
	answers: Record<string, unknown>
): string | undefined => resolveFirstUseField(schema, answers)?.id;

/**
 * The date the schedulers must not send before: first use where it can be identified, since a send *is*
 * the material going into use, and otherwise the review answer, which the send date has to respect
 * regardless.
 */
export const findSendFloorAnswer = (
	schema: IRegEdMetadataForm | null | undefined,
	answers: Record<string, unknown>
): string | null => {
	const firstUse = resolveFirstUseField(schema, answers);
	const answer = firstUse?.id ? asIsoDay(answers[firstUse.id]) : null;
	return answer ?? findReviewCompleteAnswer(schema, answers);
};

/**
 * The date of first use when it answers earlier than review completion — the state the pickers prevent
 * but a typed answer can still reach. Returned for the submit path to refuse, since nothing downstream
 * bounds it: the send floor is clamped against today, never against the review date.
 */
export const findDateBeforeReviewComplete = (
	schema: IRegEdMetadataForm | null | undefined,
	answers: Record<string, unknown>
): { field: IRegEdMetadataField; reviewCompleteDate: string } | null => {
	const reviewCompleteDate = findReviewCompleteAnswer(schema, answers);
	const firstUse = resolveFirstUseField(schema, answers);
	if (!reviewCompleteDate || !firstUse?.id) {
		return null;
	}
	const answer = asIsoDay(answers[firstUse.id]);
	return answer && answer < reviewCompleteDate ? { field: firstUse, reviewCompleteDate } : null;
};

/**
 * Brings the date of first use up to the review-completion date, seeding it when blank: material
 * cannot go into use before the review that clears it has finished.
 *
 * Only that one question — never every date question. A form can ask for dates that legitimately
 * precede review (an expiration, a prior approval), and answering one of those on the advisor's behalf
 * would file a wrong date *and* silence the required-field prompt for it, since it would no longer read
 * as empty. Undefined when nothing needs changing.
 */
/**
 * The first day on or after `day` that the channel can actually act on, as `yyyy-MM-dd`.
 *
 * The step is capped: `unavailableDaysOfWeek` comes from the account's own settings, so a whole week
 * can never be unavailable, but a bad configuration must not spin here.
 */
export const nextAvailableDay = (day: string, unavailableDaysOfWeek?: number[]): string => {
	if (!unavailableDaysOfWeek?.length) {
		return day;
	}
	const candidate = moment(day, 'YYYY-MM-DD', true);
	if (!candidate.isValid()) {
		return day;
	}
	let steps = 0;
	while (unavailableDaysOfWeek.includes(candidate.day()) && steps < 7) {
		candidate.add(1, 'day');
		steps += 1;
	}
	return candidate.format('YYYY-MM-DD');
};

export const applyReviewDateFloorAnswers = (
	schema: IRegEdMetadataForm | null | undefined,
	answers: Record<string, unknown>,
	unavailableDaysOfWeek?: number[]
): Record<string, unknown> | undefined => {
	const reviewDate = findReviewCompleteAnswer(schema, answers);
	// First use is the send, so seeding the review date verbatim can land it on a day the account
	// cannot send — the same day the field's own picker refuses. Carried forward to the next day it
	// can, which for an account that works weekends is the review date itself.
	const floor = reviewDate ? nextAvailableDay(reviewDate, unavailableDaysOfWeek) : null;
	const firstUse = resolveFirstUseField(schema, answers);
	if (!floor || !firstUse?.id) {
		return undefined;
	}
	const answer = answers[firstUse.id];
	const below = asIsoDay(answer);
	if (!isAnswerEmpty(answer) && !(below && below < floor)) {
		return undefined;
	}
	return { ...answers, [firstUse.id]: floor };
};

/**
 * The advisor's answered review-completion date. A server-set `minimumDate` is the only tenant-safe
 * handle on that question: field ids and labels are the broker-dealer's, and the other date questions
 * are indistinguishable `Date` fields.
 */
export const findReviewCompleteAnswer = (
	schema: IRegEdMetadataForm | null | undefined,
	answers: Record<string, unknown>
): string | null => {
	for (const field of orderedActiveFields(schema)) {
		if (!field.minimumDate || !field.id) {
			continue;
		}
		const answer = asIsoDay(answers[field.id]);
		if (answer) {
			return answer;
		}
	}
	return null;
};

const findReviewField = (schema: IRegEdMetadataForm | null | undefined): IRegEdMetadataField | undefined =>
	orderedActiveFields(schema).find(field => !!field.id && !!asIsoDay(field.minimumDate));

export const findReviewFieldId = (schema: IRegEdMetadataForm | null | undefined): string | undefined =>
	findReviewField(schema)?.id;

/** The earliest review date the firm accepts without a short-notice reason, counted by the server from today. */
export const findReviewMinimumDate = (schema: IRegEdMetadataForm | null | undefined): string | null =>
	asIsoDay(findReviewField(schema)?.minimumDate);

/** The review question, when its answer falls after `deadline`. */
export const findReviewAfterDeadline = (
	schema: IRegEdMetadataForm | null | undefined,
	answers: Record<string, unknown>,
	deadline: string
): string | undefined => {
	const reviewField = findReviewField(schema);
	const answer = reviewField?.id ? asIsoDay(answers[reviewField.id]) : null;
	return answer && answer > deadline ? reviewField!.id : undefined;
};

/** Pulls a review answer after `deadline` back to it; the same object when nothing moves. */
export const capReviewAnswer = (
	schema: IRegEdMetadataForm | null | undefined,
	answers: Record<string, unknown>,
	deadline: string | undefined
): Record<string, unknown> => {
	const fieldId = deadline ? findReviewAfterDeadline(schema, answers, deadline) : undefined;
	return fieldId ? { ...answers, [fieldId]: deadline } : answers;
};

/**
 * A revision's dates are derived rather than re-asked. The prior round's review date has gone stale — the
 * minimum is recomputed per request, so replaying it always reads as short notice — and first use is the
 * day the revision actually sends. Review is capped at the send day, so a send the advisor deliberately
 * brings inside the notice window still files first use on or after review, and asks for the reason.
 * Without a send day (a blog, which may be backdated) first use is left to the advisor.
 */
export const applyRevisionDateAnswers = (
	schema: IRegEdMetadataForm | null | undefined,
	answers: Record<string, unknown>,
	sendDay: string | null
): Record<string, unknown> | undefined => {
	const next = { ...answers };
	const reviewField = findReviewField(schema);
	const minimum = findReviewMinimumDate(schema);
	if (reviewField?.id && minimum) {
		const prior = asIsoDay(answers[reviewField.id]);
		const refreshed = prior && prior > minimum ? prior : minimum;
		next[reviewField.id] = sendDay && sendDay < refreshed ? sendDay : refreshed;
	}
	const firstUse = resolveFirstUseField(schema, next);
	if (firstUse?.id && sendDay) {
		next[firstUse.id] = sendDay;
	}
	const changed = Object.keys(next).some(id => next[id] !== answers[id]);
	return changed ? next : undefined;
};

/** The questions `applyRevisionDateAnswers` owns, which the advisor changes through the send date instead. */
export const revisionLockedFieldIds = (
	schema: IRegEdMetadataForm | null | undefined,
	answers: Record<string, unknown>,
	hasSendDay: boolean
): { reviewFieldId?: string; firstUseFieldId?: string } => ({
	reviewFieldId: findReviewMinimumDate(schema) ? findReviewField(schema)?.id : undefined,
	firstUseFieldId: hasSendDay ? resolveFirstUseField(schema, answers)?.id : undefined,
});

/**
 * The field whose answered date falls before its server-computed `minimumDate`, if any. Compared as
 * `yyyy-MM-dd` strings — parsing through a UTC `Date` reintroduces the off-by-one that `minimumDate`
 * exists to avoid.
 *
 * Deliberately keeps its own loop rather than sharing one with `findReviewCompleteAnswer`: this one must
 * skip fields that meet their minimum and keep looking, so a shared "first field with a minimumDate"
 * lookup would miss a later shortfall and drop the override requirement.
 */
export const findLeadTimeShortfall = (
	schema: IRegEdMetadataForm | null | undefined,
	answers: Record<string, unknown>
): { field: IRegEdMetadataField; minimumDate: string } | null => {
	for (const field of orderedActiveFields(schema)) {
		const minimumDate = field.minimumDate;
		if (!minimumDate || !field.id) {
			continue;
		}
		const answer = answers[field.id];
		if (typeof answer === 'string' && answer.length === minimumDate.length && answer < minimumDate) {
			return { field, minimumDate };
		}
	}
	return null;
};
